import React, { useState, useEffect, useRef } from 'react'
import { useRouteMatch, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { callAPI, getTheme } from 'helpers'
import qs from 'qs'
import { compact, keyBy, maxBy } from 'lodash'

// Components
import { Button, Empty, Icon } from 'antd'
import ContentWrapper from 'components/ContentWrapper'

import ExamCreateModal from 'components/exams/ExamCreateModal'
import ExamEditModal from 'components/exams/ExamEditModal'

import QuestionItem from 'components/QuestionItem'
import Spinner from 'components/Spinner'
import QuestionCreateModal from 'components/exams/QuestionCreateModal'
import QuestionEditModal from 'components/exams/QuestionEditModal'
import GridLayout from 'react-grid-layout'

export default function () {
  const match = useRouteMatch()
  const location = useLocation()
  const containerRef = useRef()

  const { _class, _course } = qs.parse(location.search.slice(1))
  const examId = match.params.examId

  const [examEditVisible, setExamEditVisible] = useState(false)
  const [questionCreateVisible, setQuestionCreateVisible] = useState(false)
  const [editedQuestion, setEditedQuestion] = useState(null)
  const [editedQuestionNo, setEditedQuestionNo] = useState(null)

  const [loading, setLoading] = useState(false)
  const [examDetail, setExamDetail] = useState({})
  const [questions, setQuestions] = useState([])
  const [repositioning, setRepositioning] = useState()
  const [draggable, setDraggable] = useState()

  const questionMap = keyBy(questions, ({ examQuestionId }) => examQuestionId)
  const nextSequence = questions.length
    ? Math.floor(maxBy(questions, 'sequence').sequence + 1)
    : 1

  useEffect(() => {
    const fetchExamDetail = async () => {
      console.log('fetchExamDetail starting..')
      try {
        setLoading(true)
        const { result: { examDetail, questions } } = await callAPI({ url: `/exams/${examId}` })
        setExamDetail(examDetail)
        setQuestions(questions)
        setLoading(false)
      } catch (error) {
        console.error('Error while trying to fetch exam detail.', error.message)
        setLoading(false)
      }
    }

    if (examId && examId !== 'create') { fetchExamDetail() }
  }, [examId])

  if (examId === 'create') return <ExamCreateModal />

  const _onQuestionEdit = (_question, _questionNo) => {
    setEditedQuestion(_question)
    setEditedQuestionNo(_questionNo)
  }

  const _onQuestionRemove = (questionId) => {
    setQuestions(_questions => _questions.filter(({ examQuestionId }) => examQuestionId !== questionId))
  }

  const _onDragStop = async (layout, { i: key1, y: pos1 }, { i: key2, y: pos2 }) => {
    const layoutMap = keyBy(layout, ({ y }) => y)
    const itemBefore = pos2 - 1 >= 0 ? questionMap[layoutMap[pos2 - 1].i] : { sequence: 0 }
    const itemAfter = pos2 + 1 < questions.length ? questionMap[layoutMap[pos2 + 1].i] : { sequence: nextSequence }
    const newSequence = (itemBefore.sequence + itemAfter.sequence) / 2

    try {
      setRepositioning(true)
      await callAPI({
        method: 'PUT',
        url: `/exams/${examId}/questions/${key1}/positions`,
        body: { sequence: newSequence }
      })
      setRepositioning(false)
    } catch (error) {
      console.error('Error while trying to reposition a question.', error)
      setRepositioning(false)
    }
  }

  const layout = questions.map(question => ({
    i: `${question.examQuestionId}`,
    x: 0,
    y: 0,
    w: 1,
    h: 1
  }))

  const panelLeft = `calc(50vw + ${10 + (containerRef.current ? (containerRef.current.offsetWidth / 2) : 0)}px)`

  return (
    <GridContainer ref={containerRef} style={{ padding: '20px 0px' }}>
      {loading ? (
        <ContentWrapper style={{ marginTop: 30 }}>
          <Spinner />
        </ContentWrapper>
      ) : (
        <div>
          <ExamDetailWrapper>
            <ExamTitle>{examDetail.title}</ExamTitle>
            <ExamCourse>{examDetail.courseCode} : {examDetail.courseName}</ExamCourse>
            <Button
              icon='edit'
              style={{ position: 'absolute', right: 20, top: 20 }}
              onClick={() => setExamEditVisible(true)}>
              <span>แก้ไขชื่อและคอร์สเรียน</span>
            </Button>
          </ExamDetailWrapper>
          {questions.length ? (
            <div>
              {!draggable ? (
                <div>
                  {questions.map((question, index) =>
                    <QuestionItem key={question.examQuestionId}
                      questionNo={index + 1}
                      question={question}
                      onEdit={_onQuestionEdit}
                      onRemove={_onQuestionRemove} />
                  )}
                </div>
              ) : (
                <GridLayout
                  layout={layout}
                  cols={1}
                  margin={[0, 0]}
                  isResizable={false}
                  rowHeight={88}
                  onDragStop={_onDragStop}
                  onLayoutChange={_layout => setQuestions(_questions => {
                    const order = _layout.sort((a, b) => a.y - b.y).slice()
                    const questionMap = keyBy(_questions, 'examQuestionId')
                    return compact(order.map(({ i }) => questionMap[i]))
                  })}
                  width={containerRef.current.offsetWidth}>
                  {questions.map((question, index) =>
                    <div key={question.examQuestionId}>
                      <QuestionItem
                        draggable
                        questionNo={index + 1}
                        question={question} />
                    </div>
                  )}
                </GridLayout>
              )}
            </div>
          ) : (
            <ContentWrapper style={{ marginTop: 20 }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span>ยังไม่มีข้อมูลคำถามของแบบฝึกหัดนี้</span>}>
                <Button type='primary' icon='plus' onClick={() => setQuestionCreateVisible(true)}>เพิ่มคำถาม</Button>
              </Empty>
            </ContentWrapper>
          )}
        </div>
      )}
      {!loading && (
        <Panel style={{ left: panelLeft }}>
          <PanelButton icon='plus' title='เพิ่มคำถาม' onClick={() => setQuestionCreateVisible(true)} />
          {!questions.length ? null : draggable ? (
            <PanelButton icon='reload' iconStyle={{ transform: 'rotateY(180deg)' }} title='กลับสู่โหมดปกติ' onClick={() => setDraggable(false)} />
          ) : (
            <PanelButton icon='drag' title='สลับตำแหน่งคำถาม' onClick={() => setDraggable(true)} />
          )}
        </Panel>
      )}
      <ExamEditModal
        visible={examEditVisible}
        classExam={examDetail}
        onSave={(_exam) => {
          setExamDetail(_exam)
          setExamEditVisible(false)
        }}
        onCancel={() => setExamEditVisible(false)} />
      <QuestionCreateModal
        examId={examId}
        visible={questionCreateVisible}
        questionNo={questions.length + 1}
        onCreate={(_question) => {
          setQuestions(_questions => _questions.concat([_question]))
          setQuestionCreateVisible(false)
        }}
        onCancel={() => setQuestionCreateVisible(false)} />
      <QuestionEditModal
        examId={examId}
        question={editedQuestion}
        questionNo={editedQuestionNo}
        onSave={(_question) => {
          setQuestions(_questions => _questions.map(_q =>
            _q.examQuestionId === _question.examQuestionId
              ? _question
              : _q
          ))
          setEditedQuestion(null)
        }}
        onCancel={() => setEditedQuestion(null)} />
    </GridContainer>
  )
}

const GridContainer = styled.div`
  @media(min-width: 800px) {
    max-width: 768px;
    margin: 0 auto;
  }
  @media(max-width: 799px) {
    margin: 0px 10px;
    width: calc(100% - 20px);
  }
`
const ExamDetailWrapper = styled(ContentWrapper)`
  position: relative;
  margin-bottom: 15px;
`
const ExamTitle = styled.h1`
  margin: 0px;
  padding: 0px;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
`
const ExamCourse = styled.div`
  color: white;
  background-color: ${getTheme`colors.gray`};
  padding: 3px 5px;
  border-radius: 4px;
  display: inline-block;
  vertical-align: top;
  font-size: 12px;
  margin-top: 5px;
`

const Panel = styled.div`
  position: fixed;
  top: 189px;
  background-color: white;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 1px -1px rgba(0,0,0,0.2), 0 1px 1px 0 rgba(0,0,0,0.141), 0 1px 3px 0 rgba(0,0,0,0.122);
`
const PanelButtonWrapper = styled.div`
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0px; }
  cursor: pointer;
  position: relative;
`
const PanelButtonRow = styled.div`
  height: 24px;
  line-height: 24px;
  transition: opacity 0.3s ease;
`
const PanelButtonIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
  color: ${props => props.hovering ? 'white' : 'black'};
  border: ${props => props.hovering ? 0 : 1}px solid ${getTheme`colors.border`};
  position: relative;
  z-index: 1;
`
const PanelButtonTitle = styled.div`
  position: absolute;
  white-space: nowrap;
  line-height: 24px;
  top: 0px;
  padding-left: 28px;
  padding-right: 8px;
  border-radius: 12px;
  font-size: 12px;
  transition: opacity 0.3s ease, color 0.3s ease;
  background-color: ${getTheme`colors.gray`};
  color: white;
  opacity: ${props => props.hovering ? 1 : 0};
`

const PanelButton = ({ icon, iconStyle, title, onClick }) => {
  const [hovering, setHovering] = useState(false)

  return (
    <PanelButtonWrapper
      onClick={onClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}>
      <PanelButtonRow>
        <PanelButtonIconWrapper hovering={hovering}>
          <Icon type={icon} style={iconStyle} />
        </PanelButtonIconWrapper>
        <PanelButtonTitle hovering={hovering}>{title}</PanelButtonTitle>
      </PanelButtonRow>
    </PanelButtonWrapper>
  )
}



// WEBPACK FOOTER //
// ./src/containers/ExamDetail.js